import React from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  RadialBarChart,
  RadialBar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { CampaignSuccessMetric } from '../../services/api';

interface MetricChartProps {
  metric: CampaignSuccessMetric;
}

// Updated color palette from design system
const COLORS = {
  primary: '#00B5F1',      // Blue
  secondary: '#34DF5A',    // Green
  warning: '#FF7488',      // Pink
  danger: '#FF3B4F',       // Red
  gray: '#F1F6F1',         // Light Gray
  purple: '#9639FF',       // Purple
  teal: '#007F7A',         // Teal
  pink: '#FF7488',         // Pink (duplicate for compatibility)
};

const CHART_COLORS = [
  COLORS.purple,     // #9639FF
  COLORS.primary,    // #00B5F1
  COLORS.teal,       // #007F7A
  COLORS.warning,    // #FF7488
  COLORS.danger,     // #FF3B4F
  COLORS.secondary,  // #34DF5A
  COLORS.gray,       // #F1F6F1
];

// Priority badge colors - aligned with new palette
const getPriorityColor = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'high':
      return 'bg-red-100 text-red-800'; // Keep red for high priority
    case 'medium':
      return 'bg-orange-100 text-orange-800'; // Use orange instead of yellow
    case 'low':
      return 'bg-green-100 text-green-800'; // Keep green for low priority
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Category badge colors - aligned with new palette
const getCategoryColor = (category: string) => {
  const categoryColors: Record<string, string> = {
    'Setup': 'bg-purple-100 text-purple-800', // Using new purple
    'Engagement': 'bg-blue-100 text-blue-800', // Using new blue
    'Resolution': 'bg-teal-100 text-teal-800', // Using new teal
    'Outcome': 'bg-green-100 text-green-800', // Using new green
    'Satisfaction': 'bg-pink-100 text-pink-800', // Using new pink
    'Follow-up': 'bg-indigo-100 text-indigo-800', // Keep indigo for follow-up
  };
  return categoryColors[category] || 'bg-gray-100 text-gray-800';
};

const MetricChart: React.FC<MetricChartProps> = ({ metric }) => {
  const renderChart = () => {
    const { chart_type, chart_data, aggregated_data } = metric;

    switch (chart_type) {
      case 'bar':
        if (!chart_data.bar) return null;
        return (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chart_data.bar.categories.map((cat, idx) => ({
              name: cat,
              value: chart_data.bar!.series[0].data[idx],
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis 
                dataKey="name" 
                tick={{ fill: 'var(--foreground-muted)' }}
                axisLine={{ stroke: 'var(--border)' }}
              />
              <YAxis 
                tick={{ fill: 'var(--foreground-muted)' }}
                axisLine={{ stroke: 'var(--border)' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--card)',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                }}
                labelStyle={{ color: 'var(--foreground)' }}
              />
              <Bar dataKey="value" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        if (!chart_data.pie) return null;
        const pieData = chart_data.pie.labels.map((label, idx) => ({
          name: label,
          value: chart_data.pie!.values[idx],
          percentage: aggregated_data.percentages?.[label] || 0,
        }));
        return (
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill={COLORS.primary}
                dataKey="value"
                label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--card)',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                }}
                labelStyle={{ color: 'var(--foreground)' }}
              />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'gauge':
        // For scale-based metrics, use a radial bar chart
        const gaugeValue = aggregated_data.average_score || 0;
        const maxValue = metric.answer_type === 'scale-5' ? 5 : 10;
        const gaugeData = [{
          name: 'Score',
          value: gaugeValue,
          fill: gaugeValue >= maxValue * 0.7 ? COLORS.secondary : 
                gaugeValue >= maxValue * 0.4 ? COLORS.warning : COLORS.danger,
        }];
        
        return (
          <div className="relative">
            <ResponsiveContainer width="100%" height={200}>
              <RadialBarChart
                cx="50%"
                cy="50%"
                innerRadius="60%"
                outerRadius="90%"
                data={gaugeData}
                startAngle={180}
                endAngle={0}
              >
                <RadialBar
                  dataKey="value"
                  cornerRadius={10}
                  fill={gaugeData[0].fill}
                  background={{ fill: 'var(--border)' }}
                  max={maxValue}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                  }}
                  labelStyle={{ color: 'var(--foreground)' }}
                />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center mt-8">
                <div className="text-3xl font-bold text-[var(--foreground)]">
                  {gaugeValue.toFixed(1)}
                </div>
                <div className="text-sm text-[var(--foreground-muted)]">
                  out of {maxValue}
                </div>
              </div>
            </div>
          </div>
        );

      case 'metric':
        // Simple metric display
        const metricValue = Object.values(aggregated_data.distribution || {})[0] || 0;
        return (
          <div className="flex items-center justify-center h-32">
            <div className="text-center">
              <div className="text-4xl font-bold text-[var(--foreground)]">
                {metricValue}
              </div>
              <div className="text-sm text-[var(--foreground-muted)] mt-1">
                Total Count
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="flex items-center justify-center h-32 text-[var(--foreground-muted)]">
            <p>Unsupported chart type: {chart_type}</p>
          </div>
        );
    }
  };

  return (
    <div className="bg-[var(--card)] rounded-lg p-6 shadow-sm border border-[var(--border)] hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-start justify-between mb-2">
          <h4 className="text-base font-semibold text-[var(--foreground)] leading-tight">
            {metric.question}
          </h4>
        </div>
        
        {/* Badges */}
        <div className="flex items-center gap-2 mt-2">
          <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(metric.priority)}`}>
            {metric.priority}
          </span>
          <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(metric.category)}`}>
            {metric.category}
          </span>
        </div>

        {/* Reason */}
        {metric.reason && (
          <p className="text-xs text-[var(--foreground-muted)] mt-2 italic">
            {metric.reason}
          </p>
        )}
      </div>

      {/* Chart */}
      <div className="mt-4">
        {renderChart()}
      </div>

      {/* Additional Stats */}
      {metric.aggregated_data.type === 'binary' && (
        <div className="mt-4 pt-4 border-t border-[var(--border)]">
          <div className="grid grid-cols-2 gap-4 text-sm">
            {Object.entries(metric.aggregated_data.percentages || {}).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <span className="text-[var(--foreground-muted)] capitalize">{key}:</span>
                <span className="font-medium text-[var(--foreground)]">{value.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricChart;
